import { ArrowLeft, ArrowRight } from 'lucide-react';

export interface LiveSequentialScoreProps {
  itemName: string;
  itemDescription?: string;
  itemIndex: number;
  totalItems: number;
  phaseTitle: string;
  /** Canonical definition of what this value means. Shown above the tool-specific scoring guidance. */
  valueDefinition?: string;
  phaseDescription: string;
  placeholder: string;
  inputValue: string;
  onInputChange: (value: string) => void;
  onNext: () => void;
  onBack?: () => void;
  canProceed: boolean;
  isLastStep: boolean;
}

export function LiveSequentialScore({
  itemName,
  itemDescription,
  itemIndex,
  totalItems,
  phaseTitle,
  valueDefinition,
  phaseDescription,
  placeholder,
  inputValue,
  onInputChange,
  onNext,
  onBack,
  canProceed,
  isLastStep,
}: LiveSequentialScoreProps) {
  const overallPct = totalItems > 0 ? Math.round(((itemIndex + (canProceed ? 0.5 : 0)) / totalItems) * 100) : 0;

  return (
    <div className="max-w-lg">
      <div className="border border-[var(--rule)] bg-[var(--surface)] p-4 mb-4">
        <div className="font-body text-[0.85rem] font-medium text-[var(--text-pri)]">{itemName}</div>
        {itemDescription && (
          <div className="font-body text-[0.75rem] text-[var(--text-ter)] mt-1">{itemDescription}</div>
        )}
      </div>

      <div className="font-heading text-[0.52rem] tracking-[0.14em] uppercase text-electric-mustard-600 mb-1">
        {phaseTitle}
      </div>
      {valueDefinition && (
        <p className="font-body text-[0.78rem] text-[var(--text-sec)] mb-1.5 leading-relaxed">{valueDefinition}</p>
      )}
      <p className="font-body text-[0.78rem] text-[var(--text-ter)] mb-4 leading-relaxed">{phaseDescription}</p>

      <label className="block font-heading text-[0.5rem] tracking-[0.1em] uppercase text-[var(--text-dim)] mb-1.5">
        Your score
      </label>
      <input
        type="text"
        inputMode="decimal"
        value={inputValue}
        onChange={e => onInputChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-[var(--rule-bright)] bg-[var(--bg-raised)] px-3 py-2.5 font-body text-[var(--text-pri)] text-sm focus:border-alpine-green-500/50 focus:outline-none focus:ring-1 focus:ring-alpine-green-500/30"
      />

      <div className="flex items-center justify-between mt-6 gap-3">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 font-heading text-[0.55rem] tracking-[0.08em] uppercase text-[var(--text-ter)] hover:text-[var(--text-sec)]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </button>
        ) : (
          <span />
        )}
        <span className="font-heading text-[0.5rem] text-[var(--text-dim)]">{overallPct}% session</span>
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          className="inline-flex items-center gap-1 h-8 px-3 font-heading text-[0.55rem] tracking-[0.1em] uppercase text-alpine-green-400 border border-alpine-green-500/45 bg-alpine-green-500/15 hover:bg-alpine-green-500/25 disabled:opacity-40"
        >
          {isLastStep ? 'Finish' : 'Next'}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
